// Device-capability layer for touch / coarse-pointer adaptation.
//
// The workspace had NO coarse-pointer detection anywhere, so every canvas
// board kept its mouse-only interaction model on touchscreens. This module is
// the single switch the iPad touch-parity fixes read — panzoom drag gates,
// long-press context menus, 44px touch targets, keyboard avoidance — exposed
// two ways:
//   • JS:  el.call('isCoarsePointer') inside any DOMQL factory
//   • CSS: <html data-pointer="coarse|fine"> (mirrors data-shell-route/data-theme)
//
// FA204/FA207 / frank trap — the memoized `_coarse` flag and the `_synced`
// one-time listener guard used to be plain module-scope `let`s, read/written
// across FOUR exported functions with no `el`/`root` argument anywhere in
// this file to hoist that state onto
// (project_frank_drops_module_local_helpers). Unlike a pure performance
// cache, losing this state actually changes behavior (isCoarsePointer would
// silently return the wrong value once `_coarse` reads as `undefined`
// post-frank — `undefined !== null` is true, so the memo branch would
// short-circuit and return `undefined` instead of ever computing the real
// value; `_synced` reset to false on every call would re-register the
// media-query listener every time). Re-homed onto
// `window.__pointerCapabilityState` (lazily created, inlined identically in
// each export below — a shared getter function would just relocate the same
// bare-sibling-call hazard) — the same always-reachable-global escape hatch
// the shell's own `window.__smblsPathPatched` sentinel uses, since there's
// no `el`/`root` argument here to use `el.context`/`root.__x` the way other
// files in this sweep do.

// True on touch-primary devices (iPad, phones). Uses the primary-pointer media
// query OR maxTouchPoints, so a hybrid device with a touchscreen still opts in.
// Memoized; call refreshPointerCapability() to re-evaluate after a pointer
// change (e.g. an iPad Magic Keyboard trackpad attaching/detaching).
export const isCoarsePointer = () => {
  if (typeof window === 'undefined') return false
  if (!window.__pointerCapabilityState)
    window.__pointerCapabilityState = { coarse: null, synced: false }
  const state = window.__pointerCapabilityState
  if (state.coarse !== null) return state.coarse
  const coarseMq = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
  const touch = typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0
  state.coarse = coarseMq || touch
  return state.coarse
}

export const refreshPointerCapability = () => {
  if (typeof window === 'undefined') return false
  if (!window.__pointerCapabilityState)
    window.__pointerCapabilityState = { coarse: null, synced: false }
  const state = window.__pointerCapabilityState
  state.coarse = null
  const coarseMq = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
  const touch = typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0
  state.coarse = coarseMq || touch
  return state.coarse
}

// Pan/zoom navigation hint — the canvas boards (studios / tickets / team / infra)
// and the analytics map all surface a "how to move around" hint. The mouse
// wording ("Wheel to pan, ⌘/Ctrl + wheel to zoom") is wrong on a touch device,
// so return the instruction that matches the active pointer. Use via
// `el.call('panZoomHint')` inside a DOMQL text factory.
// `function`, not an arrow: `el.call` binds the element, and the sentence reads
// in the SESSION language (it was English on a Georgian board, live walk
// 2026-09-21). A caller with no element keeps the English sentence.
export function panZoomHint() {
  const el = this && typeof this.call === 'function' ? this : null
  const sentence = (key, fallback) => {
    const text = el ? el.call('polyglot', key) : null
    return text && text !== key ? text : fallback
  }
  let coarse = false
  if (typeof window !== 'undefined') {
    if (!window.__pointerCapabilityState)
      window.__pointerCapabilityState = { coarse: null, synced: false }
    const state = window.__pointerCapabilityState
    if (state.coarse !== null) {
      coarse = state.coarse
    } else {
      const coarseMq = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
      const touch = typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0
      coarse = coarseMq || touch
      state.coarse = coarse
    }
  }
  return coarse
    ? sentence('common.hint.panTouch', 'Drag to pan, pinch to zoom.')
    : sentence('common.hint.panWheel', 'Wheel to pan, ⌘/Ctrl + wheel to zoom.')
}

// Reflect the capability onto <html data-pointer> so CSS can branch without JS
// (mirrors the data-shell-route / data-theme attribute pattern in the shell).
// Idempotent — safe to call on every route sync; the media listener is wired
// exactly once. Call at shell boot.
export const syncPointerCapability = () => {
  if (typeof document === 'undefined') return
  if (!window.__pointerCapabilityState)
    window.__pointerCapabilityState = { coarse: null, synced: false }
  const state = window.__pointerCapabilityState
  const apply = () => {
    state.coarse = null
    const coarseMq = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
    const touch = typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0
    state.coarse = coarseMq || touch
    document.documentElement.setAttribute('data-pointer', state.coarse ? 'coarse' : 'fine')
  }
  apply()
  if (state.synced) return
  state.synced = true
  try {
    const mq = window.matchMedia && window.matchMedia('(pointer: coarse)')
    if (mq && mq.addEventListener) mq.addEventListener('change', apply)
  } catch (err) {
    console.warn('[pointerCapability] media listener failed', err)
  }
}
